import React, {useState, useRef, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import {
  handlePromptCheckF1,
  handlePromptCheckF2,
  handlePromptCheckF3,
  processCurrentOrder,
  setMainInputFocus,
  setPrompt,
  setScanError,
  setSerialRecordInput,
  handleCartRestart,
  startPostScanningForOrder,
  submitTypeScanCount
} from '../../actions/ScanToCartScanningActions';
import PopUpModel from '../scanpack/pop_up_model';
import useWidth from './useWidth';
import ClickScanConfirmationPopUp from '../scanpack/click_scan_confirmation_pop_up';
import TypeScanAlert from '../scanpack/type_scan_alert';
import ProductConfirmationCheck from '../scanpack/productConfirmationCheck';
import i18n from 'i18n-js';
import SerialRecord from '../scanpack/serial_record';
import {validateSerialRecord, getToteSummary, parseToteIdentifier, findMatchedTote, buildScanPayload, shouldTriggerPostScan} from './utils/scanningUtils';
import audioService from '../../services/AudioService';
import { handleProductInstructionByPass } from '../../actions/scanToCartActions';
import ConfirmationDialog from '../../commons/modal/ConfirmationDialog';
import { SearchScanpackOrder } from '../../actions/scanpackAction';
import NotFulfilledItemsPrompt from '../scanToCart/NotFulfilledItemsPrompt';
import MultiToteVerificationPrompt from './MultiToteVerificationPrompt';
import PostScanTrackingPrompt from '../postScanning/PostScanTrackingPrompt';
import { getPostScanConfig, getPostScanStepsFromConfigs } from '../../utils/postScanning/postScanConfig';
import { useCameraScanner } from '../../helpers/useCameraScanner';

const CommonPrompt = () => {
  const {isVisible, message, type} = useSelector(
    state => state.scanToCartScanning.prompt
  );
  const {
    currentOrder, 
    barcodeValue, 
    nextPickItem, 
    bothSettings, 
    userInfo,
    serialRecordInputOne,
    serialRecordInputTwo,
    activeCart,
    toteAssignments,
    cartOrders
  } = useSelector(state => ({
    currentOrder: state.scanToCartScanning.currentOrder,
    barcodeValue: state.scanToCartScanning.barcodeValue,
    serialRecordInputOne: state.scanToCartScanning.serialRecordInputOne,
    serialRecordInputTwo: state.scanToCartScanning.serialRecordInputTwo,
    bothSettings: state.user.bothSettings,
    nextPickItem: state.scanToCart.nextPickItem,
    userInfo: state.user.userInfo,
    activeCart: state.scanToCart.activeCart,
    toteAssignments: state.scanToCart.toteAssignments,
    cartOrders: state.scanToCart.cartOrders
  }));
  const postScanningConfig = useSelector(getPostScanConfig);
  const scanPackSettings = bothSettings?.data?.data?.scanpack_setting;

  const [inputValue, setInputValue] = useState('');

  const [error, setError] = useState({
    show: false,
    message: '',
    type: ''
  });

  const [isVerificationLoading, setIsVerificationLoading] = useState(false);

  const inputRef = useRef();
  const dispatch = useDispatch();
  const windowWidth = useWidth();
  const navigation = useNavigation();
  const { showCameraScanner, camScannerEnabled, openCamera, closeCamera } = useCameraScanner('scanToCart', { navigation });

  useEffect(() => {
    const focusRequiredTypes = [
      'ClickScanConfirmation',
      'ProductInstructionsConfirmation',
      'SerialRecordingOne',
      'SerialRecordingTwo',
      'TypeScanCount'
    ];

    if (isVisible && focusRequiredTypes.includes(type)) {
      dispatch(setMainInputFocus(false));
      if (type === 'ClickScanConfirmation' && !error.show) {
        inputRef.current?.focus();
      }
    }
  }, [isVisible, type, error.show]);

  const showError = async (errorType, errorMessage, autoClose = true) => {
    setError({
      show: true,
      message: errorMessage,
      type: errorType
    });
    dispatch(setScanError(true));
    await audioService.playErrorSound();

    if (autoClose) {
      setTimeout(() => {
        setError(prev => ({...prev, show: false}));
      }, 3000);
    }
  };

  const closeError = () => {
    setError({show: false, message: '', type: ''});
  };

  const closeAlert = () => {
    dispatch(setPrompt(false, '', ''));
    dispatch(setMainInputFocus(true));
    // Reset all states
    setInputValue('');
    setError({show: false, message: '', type: ''});
  };

  const handleInputChange = value => {
    setInputValue(value);
  };

  // Click Scan Confirmation Handler
  const handleClickScanConfirmation = () => {
    if (inputValue === '') {
      showError('clickScan', 'Please enter confirmation code');
      return;
    }

    if (userInfo.confirmation_code === inputValue) {
      closeAlert();
      dispatch(setMainInputFocus(true));
      dispatch(handlePromptCheckF1());
    } else {
      showError('clickScan', 'Invalid confirmation code');
    }
  };

  // Product Confirmation Handler
  const handleProductConfirmation = async () => {
    if (inputValue === '' || userInfo.confirmation_code !== inputValue) {
      showError(
        'productConfirmation',
        i18n.t('productConfirmationPopup.productConfirmationAlert')
      );
      //TODO need to display error image visual feedback
      return;
    }

    closeAlert();
    dispatch(setMainInputFocus(true));
    dispatch(handlePromptCheckF2());
  };

  // Serial Record Handler
  const handleSerialRecord = () => {
    const validationResult = validateSerialRecord(
      inputValue,
      scanPackSettings,
      userInfo
    );

    if (!validationResult.isValid) {
      showError('serialRecord', validationResult.errorMessage);
      setInputValue('');
      return;
    }

    serialRecordCommonFunction();
  };

  const serialRecordCommonFunction = () => {
    const item = nextPickItem?.item;

    if (item?.record_serial && item?.second_record_serial) {
      if (serialRecordInputOne === '') {
        dispatch(setSerialRecordInput(1, inputValue));
        setInputValue('');
        dispatch(handlePromptCheckF3());
      } else {
        dispatch(setSerialRecordInput(2, inputValue));
        closeAlert();
        dispatch(processCurrentOrder());
      }
    } else {
      if (item?.record_serial) {
        dispatch(setSerialRecordInput(1, inputValue));
      } else if (item?.second_record_serial) {
        dispatch(setSerialRecordInput(2, inputValue));
      }
      closeAlert();
      dispatch(processCurrentOrder());
    }
  };

  const closeSerialRecord = () => {
    closeAlert();
    dispatch(setSerialRecordInput(1, ''));
    dispatch(setSerialRecordInput(2, ''));
  };

  const closeProductConfirmationPopUp = () => {
    closeAlert();
    dispatch(handlePromptCheckF2());
    dispatch(handleProductInstructionByPass(nextPickItem?.item?.sku, nextPickItem?.tote?.order_id, userInfo.name));
  };

  const handelReprint = () => {
    closeAlert();
    const toteIdentifier = parseToteIdentifier(message);
    const matchedTote = findMatchedTote(toteAssignments, toteIdentifier);
    const orderId = matchedTote?.order_id;
    const { steps } = getPostScanStepsFromConfigs(postScanningConfig, scanPackSettings);

    if (orderId && steps.length) {
      dispatch(startPostScanningForOrder(orderId, steps, message, { reprint: true }));
      return;
    }

    const payload = {
      app: 'app',
      input: message,
      scan_to_cart_enabled: true,
      state: 'scanpack.rfo',
      reprint: 'true'
    };
    dispatch(SearchScanpackOrder(payload));
  };

  const handleMultiToteVerification = (input) => {
    setIsVerificationLoading(true);

    const toteIdentifier = parseToteIdentifier(input);
    const matchedTote = findMatchedTote(toteAssignments, toteIdentifier);
    if (!matchedTote) {
      setIsVerificationLoading(false);
      return;
    }

    const orderId = matchedTote.order_id;
    const toteSummary = getToteSummary(toteAssignments, orderId);

    if (!shouldTriggerPostScan(toteSummary, toteIdentifier)) {
      dispatch(SearchScanpackOrder(buildScanPayload(input), () => {
        setIsVerificationLoading(false);
      }));
      return;
    }

    const { steps } = getPostScanStepsFromConfigs(postScanningConfig, scanPackSettings);

    if (steps.length) {
      closeAlert();
      setIsVerificationLoading(false);
      dispatch(startPostScanningForOrder(orderId, steps, input));
      return;
    }

    dispatch(SearchScanpackOrder(buildScanPayload(input), () => {
      setIsVerificationLoading(false);
    }));
  };

  const handleRestartConfirm = () => {
    closeAlert();
    dispatch(handleCartRestart(activeCart?.cart_id));
  };

  const handlePostScanContinue = (payload) => {
    const { orderId, remainingModules, toteInput, reprint } = payload || {};
    closeAlert();
    dispatch(startPostScanningForOrder(orderId, remainingModules || [], toteInput || null, { reprint }));
  };

  const renderPostScanRecordTracking = (payload) => {
    const {
      orderId,
      orderIncrementId: payloadOrderIncrementId,
      remainingModules = [],
      toteInput = null,
      reprint
    } = payload || {};

    return (
      <PostScanTrackingPrompt
        mode="record"
        orderId={orderId}
        orderNumber={payloadOrderIncrementId || orderId}
        value={inputValue}
        onChangeText={handleInputChange}
        onComplete={() => handlePostScanContinue({ orderId, remainingModules, toteInput, reprint })}
      />
    );
  };

  const renderPostScanVerifyShipping = (payload) => {
    const {
      orderId,
      orderIncrementId: payloadOrderIncrementId,
      remainingModules = [],
      toteInput = null,
      reprint
    } = payload || {};
    const order = (cartOrders || []).find(o => o.id === orderId);
    const hasTracking = !!(order?.tracking_num);

    return (
      <PostScanTrackingPrompt
        mode="verify"
        orderId={orderId}
        orderNumber={payloadOrderIncrementId || order?.increment_id || orderId}
        hasTracking={hasTracking}
        value={inputValue}
        onChangeText={handleInputChange}
        onComplete={() => handlePostScanContinue({ orderId, remainingModules, toteInput, reprint })}
      />
    );
  };

  const renderErrorPopup = () => {
    if (!error.show) return null;

    return (
      <PopUpModel
        closeAlert={closeError}
        message={error.message}
        messageTypeSuccess={false}
      />
    );
  };

  if (isVisible && type === 'ClickScanDisabled') {
    return (
      <PopUpModel
        closeAlert={closeAlert}
        message={message}
        messageTypeSuccess={false}
      />
    );
  }

  if (isVisible && type === 'ClickScanConfirmation') {
    return (
      <ClickScanConfirmationPopUp
        clickScanConfirmFun={handleClickScanConfirmation}
        clickScanConfirmInput={inputValue}
        errorMessageClickScanConfirmation={
          error.show && error.type === 'clickScan'
        }
        handleClickScanConfirmInput={handleInputChange}
        closeAlert={closeAlert}
        closeErrorAlert={closeError}
        serialFocus={inputRef}
        windowWidth={windowWidth}
      />
    );
  }

  if (isVisible && type === 'ProductInstructionsConfirmation') {
    return (
      <>
        {error.show &&
          error.type === 'productConfirmation' &&
          renderErrorPopup()}
        <ProductConfirmationCheck
          ProductConfirmationCheckFun={handleProductConfirmation}
          closeProductConfirmationCheckPopUp={closeProductConfirmationPopUp}
          order={currentOrder}
          scannedBarcode={barcodeValue}
          kitInstructions={nextPickItem?.item?.instruction || ''}
          productInstructions={nextPickItem?.item?.instruction || ''}
          ProductConfirmationCheckInput={inputValue}
          handleProductConfirmationCheckInput={handleInputChange}
        />
      </>
    );
  }

  if (
    isVisible &&
    (type === 'SerialRecordingOne' || type === 'SerialRecordingTwo')
  ) {
    return (
      <>
        {error.show && error.type === 'serialRecord' && renderErrorPopup()}
        <SerialRecord
          serialRecordFun={handleSerialRecord}
          serialRecordText={message}
          serialRecordInput={inputValue}
          handleSerialRecordInput={handleInputChange}
          closeSerialRecord={closeSerialRecord}
        />
      </>
    );
  }
  if (
    isVisible &&
    (type === 'reprint')
  ) {
    return (
      <>
        <ConfirmationDialog
          title="This tote has already been processed."
          message="Do you want to reprint the label?"
          confirmText="Yes, Reprint"
          cancelText="No, Cancel"
          onConfirm={handelReprint}
          onCancel={closeAlert}
          onClose={closeAlert}
          testID="ReprintDialog"
        />
      </>
    );
  }

  // Handle out of stock with array of not fulfilled items
  if (isVisible && type === 'out of stock' && Array.isArray(message)) {
    return (
      <NotFulfilledItemsPrompt
        items={message}
        onClose={() => dispatch(setPrompt(false, '', ''))}
      />
    );
  }

  if (isVisible && type === 'multiToteVerification') {
    return (
      <MultiToteVerificationPrompt
        toteSummary={message}
        onVerify={handleMultiToteVerification}
        onClose={closeAlert}
        isLoading={isVerificationLoading}
        windowWidth={windowWidth}
        camScannerEnabled={camScannerEnabled}
        showCameraScanner={showCameraScanner}
        onCameraOpen={openCamera}
        onCameraClose={closeCamera}
      />
    );
  }

  if (isVisible && type === 'RestartConfirmation') {
    return (
      <ConfirmationDialog
        title={'Restart Cart'}
        message={i18n.t('scanpackItem.restartAlert')}
        confirmText={i18n.t('scanToCart.ConfirmReset')}
        cancelText={i18n.t('scanToCart.CancelReset')}
        onConfirm={handleRestartConfirm}
        onCancel={closeAlert}
        onClose={closeAlert}
        testID="RestartConfirmationDialog"
      />
    );
  }

  if (isVisible && type === 'alert') {
    return (
      <PopUpModel
        closeAlert={closeAlert}
        message={message}
        messageTypeSuccess={false}
      />
    );
  }

  if (isVisible && type === 'TypeScanCount') {
    return (
      <TypeScanAlert
        closeAlert={closeAlert}
        typeScanCount={inputValue}
        typeScanCountUpdate={value => setInputValue(value)}
        submitTypeScan={() => {
          const count = inputValue;
          setInputValue('');
          dispatch(submitTypeScanCount(count));
        }}
        scanPackSetting={scanPackSettings}
        generalSetting={bothSettings?.data?.data?.general_setting}
      />
    );
  }

  if (isVisible && type === 'PostScanRecordTracking') {
    return renderPostScanRecordTracking(message || {});
  }
  if (isVisible && type === 'PostScanVerifyShipping') {
    return renderPostScanVerifyShipping(message || {});
  }

  return null;
};

export default CommonPrompt;
